import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { Property } from "@stratify/shared";
import { auth } from "../../../config/firebase";
import { updateProperty } from "../api/propertiesApi";
import { DeletePropertyDialog } from "./DeletePropertyDialog";
import { AddressInput } from "./AddressInput";
import { StrataPlanInput } from "./StrataPlanInput";

interface PropertyDetailsTabProps {
  property: Property;
}

export const PropertyDetailsTab: React.FC<PropertyDetailsTabProps> = ({
  property,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [address, setAddress] = useState(property.address || "");
  const [strataPlanNumber, setStrataPlanNumber] = useState(
    property.strataPlanNumber || ""
  );

  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
    // Reset form to current property values
    setAddress(property.address || "");
    setStrataPlanNumber(property.strataPlanNumber || "");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
    // Reset form to original values
    setAddress(property.address || "");
    setStrataPlanNumber(property.strataPlanNumber || "");
  };

  const handleSave = async () => {
    setError(null);

    // Client-side validation
    if (!address && !strataPlanNumber) {
      setError("Either address or strata plan number is required");
      return;
    }

    setLoading(true);

    try {
      if (!auth.currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      await updateProperty(auth.currentUser.uid, property.id, {
        address: address || undefined,
        strataPlanNumber: strataPlanNumber || undefined,
      });

      setIsEditing(false);
    } catch (err) {
      console.error("Failed to update property:", err);
      setError("Failed to update property");
    } finally {
      setLoading(false);
    }
  };

  const hasChanges =
    address !== (property.address || "") ||
    strataPlanNumber !== (property.strataPlanNumber || "");

  return (
    <>
      <DeletePropertyDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        property={property}
      />
      
      <Box sx={{ p: 3 }}>
        <Card sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5">Property Details</Typography>
            <Box>
              {!isEditing ? (
                <>
                  <IconButton
                    onClick={handleEditClick}
                    size="small"
                    data-testid="edit-property-button"
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => setIsDeleteDialogOpen(true)}
                    size="small"
                    color="error"
                    data-testid="delete-property-button"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={loading || !hasChanges}
                    startIcon={<SaveIcon />}
                    variant="contained"
                    size="small"
                    sx={{ mr: 1 }}
                    data-testid="save-property-button"
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    disabled={loading}
                    startIcon={<CancelIcon />}
                    variant="outlined"
                    size="small"
                    data-testid="cancel-property-edit-button"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Editable Fields */}
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Basic Information
              </Typography>
              
              {isEditing ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <AddressInput
                    value={address}
                    onChange={setAddress}
                    disabled={loading}
                  />
                  <StrataPlanInput
                    value={strataPlanNumber}
                    onChange={setStrataPlanNumber}
                    disabled={loading}
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" data-testid="property-address">
                      {property.address || "-"}
                    </Typography>
                  </Box>
                  {property.strataPlanNumber && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Strata Plan Number
                      </Typography>
                      <Typography variant="body1" data-testid="property-strata-plan">
                        {property.strataPlanNumber}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Read-only Location Fields */}
            {(property.neighbourhood || property.suburb || property.city) && (
              <>
                <Divider />
                <Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Location Information
                    <Typography variant="caption" display="block" color="text.disabled">
                      Automatically populated from address geocoding
                    </Typography>
                  </Typography>
                  
                  <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {property.neighbourhood && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Neighbourhood
                        </Typography>
                        <Typography variant="body1" data-testid="property-neighbourhood">
                          {property.neighbourhood}
                        </Typography>
                      </Box>
                    )}
                    {property.suburb && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Suburb
                        </Typography>
                        <Typography variant="body1" data-testid="property-suburb">
                          {property.suburb}
                        </Typography>
                      </Box>
                    )}
                    {property.city && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          City
                        </Typography>
                        <Typography variant="body1" data-testid="property-city">
                          {property.city}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>
    </>
  );
};